import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Loader2, FileCheck2, AlertTriangle,
  CheckCircle2, XCircle, Eye, EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { fmtServerDateTime } from './utils/datetime';
import { useAdminOtpGuard } from './useAdminOtpGuard';

const PROVIDERS = [
  { value: 'masters_india', label: 'Masters India' },
  { value: 'cleartax', label: 'ClearTax (stub)' },
];

const ENVS = [
  { value: 'sandbox', label: 'Sandbox' },
  { value: 'production', label: 'Production' },
];

export function AdminEInvoiceGSPPage() {
  const { admin, loading: authLoading } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const [form, setForm] = useState({
    gsp_provider_name: 'masters_india',
    api_base_url: 'https://commonapi.mastersindia.co',
    environment: 'sandbox',
    client_id: '',
    client_secret: '',
    is_active_provider: true,
  });
  const [meta, setMeta] = useState({ is_secret_configured: false, last_test: null });

  // Test-connection probe creds
  const [probe, setProbe] = useState({ gstin: '', username: '', password: '' });

  // Dual-channel (Email + WhatsApp) OTP guard (Part D).
  const { guard, dialog: otpDialog } = useAdminOtpGuard();

  // Restrict to SuperAdmin + technical_expert
  const role = admin?.role;
  const allowed = role === 'superadmin' || role === 'technical_expert';

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await adminApi.get('/admin/gsp-config');
        if (cancel) return;
        const d = r.data;
        if (d) {
          setForm((f) => ({
            ...f,
            gsp_provider_name: d.gsp_provider_name,
            api_base_url: d.api_base_url,
            environment: d.environment || 'sandbox',
            client_id: d.client_id || '',
            client_secret: '',
            is_active_provider: !!d.is_active_provider,
          }));
          setMeta({
            is_secret_configured: !!d.is_secret_configured,
            last_test: {
              at: d.last_test_connection_at,
              ok: d.last_test_connection_ok,
              message: d.last_test_connection_message,
            },
          });
        }
      } catch (e) {
        if (!cancel) toast.error(e?.response?.data?.detail || 'Failed to load GSP config');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const save = async () => {
    await guard({
      requestOtpUrl: '/admin/gsp-config/request-otp',
      title: 'Confirm GSP save',
      description: 'Saving E-Invoicing GSP credentials needs dual Email + WhatsApp OTP verification.',
      performSave: async (headers) => {
        setSaving(true);
        try {
          await adminApi.put('/admin/gsp-config', form, { headers });
          toast.success('GSP configuration saved.');
          setForm((f) => ({ ...f, client_secret: '' }));
          setMeta((m) => ({ ...m, is_secret_configured: true }));
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const test = async () => {
    if (!probe.gstin || !probe.username || !probe.password) {
      toast.error('Enter test GSTIN + einvoice username + password.');
      return;
    }
    setTesting(true);
    try {
      const r = await adminApi.post('/admin/gsp-config/test', probe);
      if (r.data.ok) toast.success('Test connection succeeded.');
      else toast.error(r.data.message || 'Test connection failed');
      setMeta((m) => ({ ...m, last_test: { at: new Date().toISOString(), ok: r.data.ok, message: r.data.message } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  if (authLoading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;
  if (!allowed) return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/admin" className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800" data-testid="gsp-back">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <h1 className="text-xl font-bold">E-Invoicing GSP Settings</h1>
            <p className="text-sm text-slate-400">Global GST Suvidha Provider configuration</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {form.environment === 'production' && (
          <div className="rounded-lg border border-rose-700 bg-rose-950/40 p-4 flex items-start gap-3" data-testid="gsp-prod-banner">
            <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-bold text-rose-200">PRODUCTION mode — IRNs generated here are legally binding.</p>
              <p className="text-rose-300/80 mt-1">Switch back to <strong>Sandbox</strong> while testing. Cancellation is only allowed within 24 hours of generation.</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-6 space-y-4">
              <h2 className="text-lg font-semibold flex items-center gap-2"><FileCheck2 className="w-4 h-4 text-amber-400" /> GSP credentials</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Provider</Label>
                  <select
                    value={form.gsp_provider_name}
                    onChange={(e) => setForm({ ...form, gsp_provider_name: e.target.value })}
                    className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm"
                    data-testid="gsp-provider"
                  >
                    {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Environment</Label>
                  <select
                    value={form.environment}
                    onChange={(e) => setForm({ ...form, environment: e.target.value })}
                    className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm"
                    data-testid="gsp-environment"
                  >
                    {ENVS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">API Base URL</Label>
                  <Input
                    value={form.api_base_url}
                    onChange={(e) => setForm({ ...form, api_base_url: e.target.value })}
                    placeholder="https://commonapi.mastersindia.co"
                    className="mt-1 bg-slate-800 border-slate-700"
                    data-testid="gsp-base-url"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Client ID</Label>
                  <Input
                    value={form.client_id}
                    onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                    className="mt-1 bg-slate-800 border-slate-700 font-mono text-xs"
                    data-testid="gsp-client-id"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider flex items-center gap-2">
                    Client Secret
                    {meta.is_secret_configured && <span className="text-emerald-400 text-[10px]">configured</span>}
                  </Label>
                  <div className="relative">
                    <Input
                      type={showSecret ? 'text' : 'password'}
                      value={form.client_secret}
                      onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                      placeholder={meta.is_secret_configured ? '•••••••• (leave blank to keep)' : 'paste secret'}
                      className="mt-1 bg-slate-800 border-slate-700 font-mono text-xs pr-10"
                      data-testid="gsp-client-secret"
                    />
                    <button type="button" onClick={() => setShowSecret(!showSecret)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500" tabIndex={-1}>
                      {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <label className="md:col-span-2 flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    checked={form.is_active_provider}
                    onChange={(e) => setForm({ ...form, is_active_provider: e.target.checked })}
                    data-testid="gsp-active"
                  />
                  Use this provider as the active GSP for the platform
                </label>
              </div>
              <div className="flex justify-end pt-2">
                <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-950" data-testid="gsp-save">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save configuration
                </Button>
              </div>
            </section>

            <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-6 space-y-4">
              <div>
                <h2 className="text-lg font-semibold">Test connection</h2>
                <p className="text-sm text-slate-400 mt-1">Calls the active GSP's auth endpoint with a test GSTIN + einvoice portal credentials. Use any Pro org's creds you have on hand.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input placeholder="Test GSTIN" value={probe.gstin} onChange={(e) => setProbe({ ...probe, gstin: e.target.value.toUpperCase() })} className="bg-slate-800 border-slate-700 font-mono" data-testid="gsp-test-gstin" />
                <Input placeholder="einvoice username" value={probe.username} onChange={(e) => setProbe({ ...probe, username: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="gsp-test-username" />
                <Input type="password" placeholder="einvoice password" value={probe.password} onChange={(e) => setProbe({ ...probe, password: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="gsp-test-password" />
              </div>
              <div className="flex items-center justify-between gap-4 pt-2 flex-wrap">
                {meta.last_test?.at ? (
                  <div className="text-xs text-slate-400 flex items-center gap-2">
                    {meta.last_test.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-rose-400" />}
                    Last tested {fmtServerDateTime(meta.last_test.at)} — {meta.last_test.message || (meta.last_test.ok ? 'OK' : 'failed')}
                  </div>
                ) : <div />}
                <Button onClick={test} disabled={testing} variant="outline" className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200" data-testid="gsp-test-btn">
                  {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Test connection
                </Button>
              </div>
            </section>
          </>
        )}
      </main>
      {otpDialog}
    </div>
  );
}

export default AdminEInvoiceGSPPage;
